import { useMutation, useQueryClient } from "@tanstack/react-query";
import fetchToBackend from "@/utils/fetchToBackend";
import { adminKeys, teamKeys } from "@/lib/query-keys";

/**
 * Acciones de admin sobre la suscripción y la cuota de un usuario. Las tres
 * invalidan el listado para que la tabla refleje el nuevo estado.
 */

function useInvalidateAdminUsers() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: adminKeys.users });
}

/** Asigna, renueva o extiende la suscripción: deja la vigencia en `expiresat`. */
export function useSetUserSubscription() {
  const invalidate = useInvalidateAdminUsers();
  return useMutation({
    mutationFn: ({
      userId,
      planId,
      expiresat,
    }: {
      userId: string;
      planId: number;
      expiresat: string;
    }) =>
      fetchToBackend(`/admin/users/${userId}/subscription`, {
        method: "PUT",
        body: JSON.stringify({ planId, expiresat }),
      }),
    onSuccess: invalidate,
  });
}

/** Cancela la suscripción: el usuario pierde el acceso en su próximo request. */
export function useCancelUserSubscription() {
  const invalidate = useInvalidateAdminUsers();
  return useMutation({
    mutationFn: (userId: string) =>
      fetchToBackend(`/admin/users/${userId}/subscription/cancel`, {
        method: "POST",
      }),
    onSuccess: invalidate,
  });
}

/** Fija el saldo de vistas del mes (valor absoluto, no delta). */
export function useSetUserViews() {
  const invalidate = useInvalidateAdminUsers();
  return useMutation({
    mutationFn: ({ userId, views }: { userId: string; views: number }) =>
      fetchToBackend(`/admin/users/${userId}/views`, {
        method: "PUT",
        body: JSON.stringify({ views }),
      }),
    onSuccess: invalidate,
  });
}

/**
 * Da de alta a un comercial dentro de una organización que ya existe. Invalida
 * también las organizaciones, donde el equipo se ve completo.
 */
export function useCreateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string;
      name: string;
      password: string;
      organizationId: string;
      // Sin esto hereda TODAS las líneas de la empresa. Nunca se omite a la
      // ligera: una cuenta sin líneas asignadas no ve nada y no da error.
      searchProfileIds?: number[];
    }) =>
      fetchToBackend("/admin/users/team-member", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.users });
      qc.invalidateQueries({ queryKey: adminKeys.organizations });
      // Si el alta fue en la organización de quien está mirando, su propia cuenta
      // acaba de volverse "de equipo": sin esto el front sigue 5 min con el
      // `staleTime` de `useTeam` y no aparece nada.
      qc.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}
